import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  CircularProgress,
  Alert,
  CardActions
} from '@mui/material';
import hosannaLogo from '../../assets/hosanna_logo.png';
import { apiClient } from '../../lib/apiClient';

function NoChurchSelected({ user, message = "Please join a church first to continue" }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allChurches, setAllChurches] = useState([]);
  const [myChurches, setMyChurches] = useState([]);
  const [loadingChurches, setLoadingChurches] = useState(false);
  const [joining, setJoining] = useState(null);

  const handleJoinChurch = async () => {
    setDialogOpen(true);
    setLoadingChurches(true);
    try {
      const [all, my] = await Promise.all([
        apiClient.getAllChurches(),
        apiClient.getMyChurches(user.id)
      ]);
      setAllChurches(all);
      setMyChurches(my);
    } catch (err) {
      console.error('Error loading churches:', err);
      alert(err.message);
    } finally {
      setLoadingChurches(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSelectChurch = (church) => {
    localStorage.setItem('selectedChurch', JSON.stringify(church));
    setDialogOpen(false);
    window.dispatchEvent(new Event('churchSelected'));
    window.location.reload(); // Reload to refresh the page state
  };

  const handleJoinChurchFromDialog = async (churchId) => {
    setJoining(churchId);
    try {
      await apiClient.joinChurch(churchId, user.id);
      const my = await apiClient.getMyChurches(user.id);
      setMyChurches(my);
      
      // Auto-select the newly joined church
      const joinedChurch = my.find(c => c.id === churchId);
      if (joinedChurch) {
        localStorage.setItem('selectedChurch', JSON.stringify(joinedChurch));
        window.dispatchEvent(new Event('churchSelected'));
        setDialogOpen(false);
        window.location.reload(); // Reload to refresh the page state
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setJoining(null);
    }
  };

  const myChurchIds = myChurches.map(c => c.id);
  const availableChurches = allChurches.filter(c => !myChurchIds.includes(c.id));

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        px: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: 3,
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              component="img"
              src={hosannaLogo}
              alt="Hosanna Logo"
              sx={{
                maxWidth: '120px',
                maxHeight: '120px',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2
            }}
          >
            Join a Church
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 3,
              lineHeight: 1.6
            }}
          >
            {message}
          </Typography>

          {/* Additional info */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              fontStyle: 'italic'
            }}
          >
            Browse available churches and become part of a community
          </Typography>

          {/* Action Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleJoinChurch}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            Join Church
          </Button>
        </CardContent>
      </Card>

      {/* Church Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Church</DialogTitle>
        <DialogContent>
          {loadingChurches ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {myChurches.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You haven't joined any churches yet. Browse available churches below.
                </Alert>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    My Churches
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {myChurches.map((church) => (
                      <Grid item xs={12} sm={6} md={4} key={church.id}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Avatar src={church.logoUrl} sx={{ width: 50, height: 50 }}>
                                {church.name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">{church.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {church.address?.city}, {church.address?.state}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                          <CardActions>
                            <Button
                              variant="contained"
                              fullWidth
                              size="small"
                              onClick={() => handleSelectChurch(church)}
                            >
                              Select
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {availableChurches.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Available Churches
                  </Typography>
                  <Grid container spacing={2}>
                    {availableChurches.map((church) => (
                      <Grid item xs={12} sm={6} md={4} key={church.id}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Avatar src={church.logoUrl} sx={{ width: 50, height: 50 }}>
                                {church.name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">{church.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {church.address?.city}, {church.address?.state}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                          <CardActions>
                            <Button
                              variant="outlined"
                              fullWidth
                              size="small"
                              onClick={() => handleJoinChurchFromDialog(church.id)}
                              disabled={joining === church.id}
                            >
                              {joining === church.id ? <CircularProgress size={20} /> : 'Join Church'}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NoChurchSelected;

